import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientAuthContext = createContext(null);

export const useClientAuth = () => {
  const context = useContext(ClientAuthContext);
  if (!context) {
    throw new Error('useClientAuth must be used within a ClientAuthProvider');
  }
  return context;
};

// Axios instance for client portal
export const clientApi = axios.create({
  baseURL: `${API_URL}/api/client-portal`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Error formatter
export function formatApiError(error) {
  const detail = error?.response?.data?.detail;
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const ClientAuthProvider = ({ children }) => {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await clientApi.get('/me');
      setClient(data);
    } catch (error) {
      setClient(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await clientApi.post('/login', { email, password });
    setClient(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await clientApi.post('/logout');
    setClient(false);
  }, []);

  const value = useMemo(() => ({
    client, loading, login, logout, checkAuth, setClient
  }), [client, loading, login, logout, checkAuth, setClient]);

  return (
    <ClientAuthContext.Provider value={value}>
      {children}
    </ClientAuthContext.Provider>
  );
};
